// HOLDOUT: SkeletonThumbnail / SkeletonBodyText are Polaris REACT components — there is
// no s-* skeleton web component. Same holdout TemplateGrid.jsx and PageSkeleton.jsx make.
import { SkeletonThumbnail, SkeletonBodyText } from "@shopify/polaris";

import PageSkeleton from "./PageSkeleton";

/**
 * The loading picture for /templates, drawn while the route chunk downloads.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS ONE IS HAND-ROLLED INSTEAD OF `sections={[…]}`
 * ─────────────────────────────────────────────────────────────────────────────
 * /templates is not a stack of text sections. Above the fold it is a filter bar
 * over a responsive CARD GRID, and a section-stack skeleton in front of a card
 * grid is a guaranteed layout jump — which is worse than the spinner it replaces.
 * `PageSkeleton`'s `children` escape hatch exists for exactly this case (see its
 * JSDoc, which names the template grid).
 *
 * The Template Library ALREADY had a good skeleton, but only for its INNER grid:
 * `useTemplateLibrary.js` exposes `loading` for the first load only and
 * `TemplateGrid.jsx`'s `if (loading)` branch draws 8 placeholder cards. What was
 * missing was everything OUTSIDE that grid — while the chunk downloaded, App.jsx
 * showed `RouteLoader`'s centred spinner or a generic `BootShell`, neither of
 * which carries the heading or the grid geometry. So the merchant got a generic
 * frame, then a card grid somewhere else.
 *
 * ⚠️ THE GRID BELOW MUST STAY IN STEP WITH TemplateGrid.jsx's OWN LOADING BRANCH
 * (TemplateGrid.jsx:62-80). These two are ADJACENT FRAMES in a single handoff —
 * this one paints while the chunk downloads, that one paints while the first
 * template query runs — so any difference between them is a visible swap in the
 * middle of one wait. Same column template, same 8 cards, same card composition.
 *
 * ⚠️ `heading` and `inlineSize` must stay byte-identical to routes/templates.jsx's
 * own `<s-page heading="Template Library" inlineSize="large">`. `inlineSize` is
 * not optional here: /templates is one of the two wide pages, and omitting it
 * draws a narrower page that snaps wider at handoff.
 *
 * NOT DRAWN, deliberately: the AI generator card. It is `lazy()` behind
 * `<Suspense fallback={null}>`, so it is genuinely absent from the real page's first
 * paint too — skeletoning it would reserve space for a block that is not there yet,
 * inventing a jump rather than removing one. It now also renders BELOW the grid and
 * the pager, so it is off-screen at first paint regardless. Same reasoning for the
 * pagination control and the "Pro tip" banner: both sit below the grid, well below
 * the fold, and the pager renders nothing at all when there is one page.
 */

/** Kept equal to TemplateGrid.jsx:22's GRID_COLUMNS — see the warning above. */
const GRID_COLUMNS = "repeat(auto-fit, minmax(360px, 1fr))";

/** Kept equal to the card count in TemplateGrid.jsx's loading branch. */
const PLACEHOLDER_CARDS = 8;

/**
 * Height of a card's preview slot — THE number that makes this skeleton the right size.
 *
 * ⚠️ A `SkeletonThumbnail size="large"` on its own is ~80 px and is NOT the card's preview.
 * The real card pins the slot itself and puts the thumbnail inside it:
 *
 *   TemplateCard.jsx:228-231   <s-box background="subdued" minBlockSize={`${previewH}px`}>
 *   TemplateCard.jsx:112       const previewH = isMobile ? 460 : 220;
 *   TemplateLibrary.jsx:74     useState('mobile')   ← the GRID's default viewport
 *
 * So a fresh load renders 460 px slots. Drawing a bare thumbnail instead makes every
 * skeleton card roughly a fifth of its real height — across 8 cards that is ~3,000 px of
 * missing page, and the grid visibly snaps taller the moment real cards arrive. That is
 * exactly the layout jump this component exists to prevent, so the pin is not optional.
 *
 * The real card's own comment at TemplateCard.jsx:243-246 makes the same point about its
 * two branches: the slot is the same height whichever one renders.
 */
const PREVIEW_HEIGHT = "460px";

/**
 * How many industry tabs to sketch.
 *
 * The real strip is data-driven (one tab per industry with a live count), but the
 * ROW is unconditional, so what matters is reserving its height — the individual
 * pill widths are only there to stop it reading as one grey slab.
 */
const INDUSTRY_TAB_WIDTHS = ["72px", "96px", "84px", "108px", "76px", "92px"];

/**
 * Format chips + the "Has motion" toggle.
 *
 * These are the LEFT half of the facet bar's row 3, which also carries the
 * "Campaign type" disclosure button beside them and the results summary +
 * per-page select pushed to the right edge of the same row.
 */
const FORMAT_CHIP_WIDTHS = ["88px", "76px", "104px", "80px", "132px"];

/**
 * One grey control-shaped placeholder.
 *
 * `s-box` with a `subdued` background is the whole trick — no custom CSS, and
 * every attribute below is a real BoxProps key (`background`, `borderRadius`,
 * `blockSize`, `inlineSize`, `maxInlineSize`), verified against
 * node_modules/@shopify/polaris-types/dist/polaris.d.ts.
 */
function Bar({ inlineSize, blockSize = "32px", maxInlineSize }) {
  return (
    <s-box
      background="subdued"
      borderRadius="base"
      blockSize={blockSize}
      inlineSize={inlineSize}
      maxInlineSize={maxInlineSize}
    />
  );
}

export default function TemplateLibrarySkeleton() {
  return (
    <PageSkeleton heading="Template Library" inlineSize="large" sections={[]}>
      {/*
        ONE child, carrying its own `gap="large"`, rather than several children of
        PageSkeleton's `gap="base"` stack — routes/templates.jsx spaces its body at
        `large`, and inheriting `base` here would shift everything below it upward
        by one space step at handoff.
      */}
      <s-stack direction="block" gap="large">
        {/*
          ⚠️ VERBATIM, not a grey line. This is static copy that routes/templates.jsx
          always renders (it is a body element because `s-page`'s `subtitle`
          attribute is silently dropped), so painting the real string is the exact
          match — a 1-line SkeletonBodyText is roughly half the height of a
          paragraph and would move the grid. Same contract as `heading`: if the
          route's copy changes, change this too.
        */}
        <s-paragraph color="subdued">
          Choose a starting point — every template is fully customizable
        </s-paragraph>

        {/* The library renders inside a single <s-section> with `padding="large"`. */}
        <s-section>
          <s-box padding="large">
            <s-stack direction="block" gap="large">
              {/* ── Facet bar (TemplateFacetBar.jsx), row for row ───────────── */}
              <s-stack direction="block" gap="base">
                {/* 1. Search (left) + the sort/viewport cluster (right):
                       [sort select] [Desktop|Mobile chip toggle]. The toggle used
                       to live on its own row above the grid; it is part of this row
                       now, so nothing below it needs to reserve height for it. */}
                <s-stack direction="inline" gap="small-300" justifyContent="space-between">
                  <Bar inlineSize="360px" maxInlineSize="100%" />
                  <s-stack direction="inline" gap="small-200" alignItems="center">
                    <Bar inlineSize="170px" />
                    <Bar inlineSize="180px" />
                  </s-stack>
                </s-stack>

                {/* 2. Industry tab strip */}
                <s-stack direction="inline" gap="small-200" alignItems="center">
                  {INDUSTRY_TAB_WIDTHS.map((width, index) => (
                    <Bar key={index} inlineSize={width} blockSize="28px" />
                  ))}
                </s-stack>

                {/*
                  3. One space-between row.
                     LEFT  — format chips + "Has motion" + the "Campaign type"
                             disclosure button that opens the chip panel.
                     RIGHT — "Showing 1–12 of N templates" + the per-page select.

                  Neither the campaign-type chip PANEL nor the applied-filter chips
                  row is drawn: both render only when something is expanded/applied,
                  and a fresh load lands in neither state, so there is nothing to
                  reserve — the same reasoning this file has always applied to
                  AppliedFilterChips.

                  ⚠️ DELIBERATE ASYMMETRY on the right-hand cluster. The live
                  `TemplatePaginationSummary` starts with `if (!totalCount) return
                  null` (TemplatePagination.jsx), and `totalCount` IS 0 at first
                  paint — so at the moment of handoff these two bars have no live
                  counterpart and the real row briefly renders with an empty right
                  edge. That is fine and intended: the mismatch is HORIZONTAL ONLY.
                  The row's height is set by the chips on the left either way, so
                  nothing moves vertically, and vertical jump is the only thing this
                  component exists to prevent. Reserving the space also means the
                  summary fades in without shoving the chips when the count arrives.
                */}
                <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
                  <s-stack direction="inline" gap="small-300" alignItems="center">
                    {FORMAT_CHIP_WIDTHS.map((width, index) => (
                      <Bar key={index} inlineSize={width} blockSize="28px" />
                    ))}
                    <Bar inlineSize="150px" blockSize="28px" />
                  </s-stack>
                  <s-stack direction="inline" gap="small-300" alignItems="center">
                    <Bar inlineSize="200px" blockSize="16px" />
                    <Bar inlineSize="150px" />
                  </s-stack>
                </s-stack>
              </s-stack>

              {/* ── The card grid, identical to TemplateGrid.jsx:62-80 ──────── */}
              <s-grid gridTemplateColumns={GRID_COLUMNS} gap="base">
                {[...Array(PLACEHOLDER_CARDS)].map((_, index) => (
                  <s-box key={index} padding="none" border="base" borderRadius="base" background="base">
                    {/* The height pin — see PREVIEW_HEIGHT. Mirrors TemplateCard.jsx:228-231
                        (`background="subdued"` + `minBlockSize`), NOT a bare thumbnail. */}
                    <s-box background="subdued" minBlockSize={PREVIEW_HEIGHT}>
                      <SkeletonThumbnail size="large" />
                    </s-box>
                    <s-box padding="base">
                      <s-stack direction="block" gap="small-200">
                        <SkeletonBodyText lines={1} />
                        <SkeletonBodyText lines={2} />
                      </s-stack>
                    </s-box>
                  </s-box>
                ))}
              </s-grid>
            </s-stack>
          </s-box>
        </s-section>
      </s-stack>
    </PageSkeleton>
  );
}
